import React from 'react';
import type { TFunction } from 'i18next';
import { FiAlertCircle } from 'react-icons/fi';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import { Grid } from '../../../common/components/Grid/Grid';
import {
  GridColumn,
  GridCountFunction,
  GridGetterFunction,
  GridRecord,
} from '../../../common/components/Grid/GridDataTypes';
import { TKeys, useTranslation } from '../../../common/hooks/useTranslation';
import momencik from '../../../common/momencik';
import { GetClientSurveyDto } from '../../../common/pokCore/autogenerated/pokApiClient';

import { EmailAddressWithStatus } from './EmailAddressWithStatus';
import { RepeatClientSurveyButton } from './RepeatClientSurveyButton';

interface ClientSurveySearchProps {
  getData: GridGetterFunction;
  getCount: GridCountFunction;
  onRowClick?: (key: string) => Promise<void> | void | undefined;
  withRepeat?: boolean;
  handleRefresh?: () => void;
  refresh?: boolean;
  showWarning?: boolean;
  initialOrderBy?: string;
  initialOrderDirection?: 'ASC' | 'DESC';
}

export const clientSurveyForGrid = (
  t: TFunction<'administrationLng', 'administrationLng'>,
  tk: TKeys<'administrationLng'>,
  clientSurvey: GetClientSurveyDto,
  lang: string,
  setRefresh?: (refresh: boolean) => void,
): GridRecord => {
  return {
    key: clientSurvey.id,
    values: {
      id: clientSurvey.id,
      'clientSurvey.systemNumber': clientSurvey.systemNumber?.toString() || '',
      'client.name': clientSurvey.client?.name || '',
      'survey.name':
        lang === 'PL'
          ? clientSurvey.survey?.name || ''
          : clientSurvey.survey?.nameEn || '',
      'client.nip': clientSurvey.client?.nip || '',
      'company.shortname': clientSurvey.company?.shortname || '',
      'clientSurvey.preparedDate': momencik(
        clientSurvey.preparedDate,
        'YYYY-MM-DD HH:mm:ss',
      ),
      'clientSurvey.sentDate': momencik(
        clientSurvey.sentDate,
        'YYYY-MM-DD HH:mm:ss',
      ),
      'clientSurvey.answeredDate': momencik(
        clientSurvey.answeredDate,
        'YYYY-MM-DD HH:mm:ss',
      ),
      'clientSurvey.warningLight': clientSurvey.warningLight ? (
        <div className="d-flex justify-content-center">
          <OverlayTrigger overlay={<Tooltip>{t(tk.survey.problem)}</Tooltip>}>
            <FiAlertCircle className="text-danger" size={24} />
          </OverlayTrigger>
        </div>
      ) : (
        ''
      ),
      clientsurvey_repetition: clientSurvey.repetition?.toString() || '',
      clientsurvey_active_yes_no: clientSurvey.active
        ? t(tk.common.yes)
        : t(tk.common.no),
      'email.sentTo': (
        <EmailAddressWithStatus
          email={clientSurvey.emailTo || ''}
          status={
            clientSurvey.answeredDate ? undefined : clientSurvey.emailStatus
          }
        />
      ),
      action: (
        <RepeatClientSurveyButton
          id={clientSurvey.id}
          onRefresh={() => setRefresh?.(true)}
        />
      ),
    },
    isInactive: !clientSurvey.active,
  };
};

export const ClientSurveySearch: React.FC<ClientSurveySearchProps> = props => {
  const { t, tk } = useTranslation('administrationLng');

  const columns: GridColumn[] = [
    {
      header: t(tk.common.sysNumber),
      property: 'clientSurvey.systemNumber',
    },
    { header: t(tk.common.client), property: 'client.name' },
    { header: t(tk.common.nip), property: 'client.nip' },
    ...(props.showWarning
      ? [
          {
            header: t(tk.clientSurvey.warning),
            property: 'clientSurvey.warningLight',
            noFilter: true,
            noSorting: true,
          },
        ]
      : []),
    { header: t(tk.survey.emailTo), property: 'email.sentTo' },
    { header: t(tk.survey.survey), property: 'survey.name' },
    { header: t(tk.common.company), property: 'company.shortname' },
    {
      header: t(tk.clientSurvey.preparedDate),
      property: 'clientSurvey.preparedDate',
    },
    {
      header: t(tk.clientSurvey.sentDate),
      property: 'clientSurvey.sentDate',
    },
    {
      header: t(tk.clientSurvey.answeredDate),
      property: 'clientSurvey.answeredDate',
    },
    {
      header: t(tk.clientSurvey.repetition),
      property: 'clientsurvey_repetition',
    },
    {
      header: t(tk.common.activeShe),
      property: 'clientsurvey_active_yes_no',
    },
    ...(props.withRepeat
      ? [{ header: '', property: 'action', noFilter: true, noSorting: true }]
      : []),
  ];

  return (
    <Grid
      getData={props.getData}
      getDataCount={props.getCount}
      columns={columns}
      showFilter={true}
      initialOrderColumn={props.initialOrderBy || 'clientSurvey.systemNumber'}
      initialOrderDirection={props.initialOrderDirection || 'DESC'}
      availablePageSizes={[10, 20, 50]}
      emptyGridMessage={t(tk.clientSurvey.noClientSurveys)}
      onRowClick={props.onRowClick}
      refresh={props.refresh}
      withHistory={true}
      withFilterInColumns={true}
    />
  );
};
